import type { ExtendedWorkspaceUseCase } from '../../utils/workspaceModules'

export type WorkspaceRoleTitle =
  | 'Founder / CEO'
  | 'Product Manager'
  | 'Engineer'
  | 'Designer'
  | 'Marketer'
  | 'Operator'
  | 'Student'
  | 'Researcher'
  | 'Creator'
  | 'Other'

export type WorkspaceUseCase = ExtendedWorkspaceUseCase

export type WorkspaceAudience = 'SOLO' | 'SOLO_HELPERS' | 'TEAM'

export type WorkspaceTeamSize = 'SOLO' | 'SMALL' | 'MEDIUM' | 'LARGE'

export type WorkspaceFocusArea =
  | 'PRODUCTIVITY'
  | 'LEARNING'
  | 'HEALTH'
  | 'CAREER'
  | 'SIDE_PROJECTS'
  | 'CREATIVE'
  | 'RESEARCH'

export type WorkspaceThemePref = 'DARK' | 'LIGHT' | 'SYSTEM'

export type WorkspaceSetupDraft = {
  workspaceName: string
  workspaceSlug: string
  isPublic: boolean
  roleTitle: WorkspaceRoleTitle | ''
  useCase: WorkspaceUseCase | ''
  currency: string
  audience?: WorkspaceAudience
  teamSize: WorkspaceTeamSize | ''
  focusAreas: WorkspaceFocusArea[]
  theme: WorkspaceThemePref
  aiSuggestionsEnabled: boolean
  notificationsEnabled: boolean
  gamificationEnabled: boolean
  intentText?: string
}

export const DEFAULT_WORKSPACE_SETUP_DRAFT: WorkspaceSetupDraft = {
  workspaceName: '',
  workspaceSlug: '',
  isPublic: false,
  roleTitle: '',
  useCase: '',
  currency: '',
  teamSize: '',
  focusAreas: [],
  theme: 'SYSTEM',
  aiSuggestionsEnabled: true,
  notificationsEnabled: true,
  gamificationEnabled: true,
  intentText: '',
}

export type StepId =
  | 'INTENT'
  | 'ROLE'
  | 'USE_CASE'
  | 'CURRENCY'
  | 'WHO'
  | 'FOCUS_AREAS'
  | 'APPEARANCE'
  | 'EXTRAS'
  | 'WORKSPACE_NAME'

export const TEAM_STEP_ORDER: StepId[] = [
  'INTENT',
  'ROLE',
  'USE_CASE',
  'CURRENCY',
  'WHO',
  'FOCUS_AREAS',
  'APPEARANCE',
  'EXTRAS',
  'WORKSPACE_NAME',
]

export const PERSONAL_STEP_ORDER: StepId[] = [
  'INTENT',
  'USE_CASE',
  'FOCUS_AREAS',
  'APPEARANCE',
  'EXTRAS',
]

export const stubInferFromFreeform = (input: string): Partial<WorkspaceSetupDraft> => {
  const lower = input.toLowerCase()
  const inferred: Partial<WorkspaceSetupDraft> = {
    intentText: input,
  }

  // Use cases
  if (/(shop|stock|retail|inventory|store)/.test(lower)) inferred.useCase = 'SHOP_STOCK'
  else if (/(field|technician|crew|on-site|site worker)/.test(lower)) inferred.useCase = 'FIELD_TEAM'
  else if (/(events?|venue|festival|wedding|conference)/.test(lower)) inferred.useCase = 'EVENTS'
  else if (/(tuition|coaching|tutor)/.test(lower)) inferred.useCase = 'TUITION_COACHING'
  else if (/(academy|bootcamp)/.test(lower)) inferred.useCase = 'ACADEMY_TRAINING'
  else if (/(corporate training|upskill)/.test(lower)) inferred.useCase = 'CORPORATE_TRAINING'
  else if (/(agency|client work|client project)/.test(lower)) inferred.useCase = 'AGENCY'
  else if (/(team|teammate|squad|colleagues?|coworkers?)/.test(lower)) inferred.useCase = 'TEAM_PROJECTS'
  else if (/(study|class|exam|university|school|college|semester)/.test(lower)) inferred.useCase = 'STUDY_PLANNING'
  else if (/(content|youtube|podcast|video|posts?|blog)/.test(lower)) inferred.useCase = 'CONTENT_PLANNING'
  else if (/(startup|company|founder|launch)/.test(lower)) inferred.useCase = 'STARTUP_BUILDING'
  else if (/(personal|myself|my own|goals?|habit)/.test(lower)) inferred.useCase = 'PERSONAL_GOALS'

  // Audience & Team size cues: "helpers", "staff", "my team", "alone"
  if (/(helpers?|staff|crew)/.test(lower)) {
    inferred.audience = 'SOLO_HELPERS'
    inferred.teamSize = 'SMALL'
  } else if (/(my team|our team|a team)/.test(lower)) {
    inferred.audience = 'TEAM'
    if (/(team of \d+|small team|few people|2|3|4|5)/.test(lower)) inferred.teamSize = 'SMALL'
  } else if (/(alone|solo|by myself|just me)/.test(lower)) {
    inferred.audience = 'SOLO'
    inferred.teamSize = 'SOLO'
  } else if (/(team of \d+|small team|few people|2|3|4|5)/.test(lower)) {
    inferred.teamSize = 'SMALL'
  }

  const focus: WorkspaceFocusArea[] = []
  if (/(productiv|focus|task)/.test(lower)) focus.push('PRODUCTIVITY')
  if (/(learn|study|skill)/.test(lower)) focus.push('LEARNING')
  if (/(health|fitness|gym|workout|run|marathon)/.test(lower)) focus.push('HEALTH')
  if (/(career|job|interview|promotion)/.test(lower)) focus.push('CAREER')
  if (/(side project|hobby|build)/.test(lower)) focus.push('SIDE_PROJECTS')
  if (/(creat|design|art|writing)/.test(lower)) focus.push('CREATIVE')
  if (/(research|paper|thesis|phd)/.test(lower)) focus.push('RESEARCH')
  if (focus.length) inferred.focusAreas = focus

  if (/(founder|ceo)/.test(lower)) inferred.roleTitle = 'Founder / CEO'
  else if (/(product manager|pm\b)/.test(lower)) inferred.roleTitle = 'Product Manager'
  else if (/(engineer|developer|dev\b|coder)/.test(lower)) inferred.roleTitle = 'Engineer'
  else if (/(designer|design)/.test(lower)) inferred.roleTitle = 'Designer'
  else if (/(student)/.test(lower)) inferred.roleTitle = 'Student'

  return inferred
}
